import React, { useRef } from 'react'
import { useLocation } from '@reach/router'
import classNames from 'classnames'
import {
  Box,
  Divider,
  Grid,
  Link as MUILink,
  Typography,
  useTheme,
  withStyles,
} from '@material-ui/core'

import { colors } from 'theme'
import Link from 'ui/Link'
import { useIsMobile } from 'utils/hooks'
import navigate from 'utils/navigate'
import Dialog from 'bookSuggestions/Dialog'
import { toggleDialog } from 'utils/dialog/utils'
import useSidebarLinks from './useSidebarLinks'

const getOppositeSide = side => (side === 'right' ? 'Left' : 'Right')

const redirectToNextJsProject = exactPath => {
  window.location = exactPath
}

const openInTab = (url, target) => {
  window.open(url, target).focus()
}

export const goTo = ({
  destination,
  callback,
  exactPath,
  target,
}) => async () => {
  callback && (await callback())

  if (target) {
    openInTab(exactPath || destination, target)
  } else if (exactPath) {
    redirectToNextJsProject(exactPath)
  } else {
    navigate(destination)
  }
}

const MenuItem = ({
  active,
  side,
  name,
  path = '',
  exactPath,
  icon,
  onClick,
  divider,
  target,
  external,
  collapsedIcon,
  fullWidthItem,
}) => {
  const IconComponent = icon
  const theme = useTheme()
  const isMobile = useIsMobile()
  const FullWidthComponent = fullWidthItem
  const CollapsedIcon = collapsedIcon

  if (divider) {
    return <Divider style={{ width: '70%', margin: theme.spacing(2) }} />
  }

  const iconSize = isMobile ? '25px' : '40px'
  const destination = exactPath || path

  const LinkComponent = external ? MUILink : Link
  const linkProps = {
    [external ? 'href' : 'to']: destination,
  }

  return (
    <Grid
      {...linkProps}
      container
      component={LinkComponent}
      target={target}
      onClick={onClick}
      style={{
        textDecoration: 'none',
        cursor: 'pointer',
        color: active ? colors.aqua500 : colors.gray100,
        padding: theme.spacing(0, 4),
        margin: theme.spacing(4, 0),
        flexDirection: 'row',
        flexWrap: 'nowrap',
        ...(active
          ? {
              [`border${getOppositeSide(side)}`]: `${colors.aqua500} 2px solid`,
            }
          : {}),
      }}
    >
      {CollapsedIcon && (
        <Grid className="menu-item-collapsed-item">
          <CollapsedIcon />
        </Grid>
      )}
      {FullWidthComponent && (
        <Grid className="menu-full-width-item">
          <FullWidthComponent />
        </Grid>
      )}
      {!fullWidthItem && (
        <>
          <IconComponent style={{ fontSize: iconSize }} />
          <Typography
            className="menu-item-text"
            style={{
              color: active ? colors.aqua500 : colors.gray400,
              lineHeight: iconSize,
              paddingLeft: theme.spacing(2),
              fontWeight: 500,
              fontSize: 14,
              display: 'inline',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
            }}
          >
            {name}
          </Typography>
        </>
      )}
    </Grid>
  )
}

const isActiveLink = ({ activePaths = [], path, exactPath }, pathname) =>
  [...activePaths, path].some(
    partialPath => pathname.indexOf(partialPath) >= 0,
  ) || exactPath === pathname

const SidebarMenu = ({
  userType,
  userCategory,
  side = 'left',
  open,
  classes,
  className,
  ...props
}) => {
  const dialogRef = useRef()

  const { pathname } = useLocation()
  const menuClass = classNames({ open }, classes.menu, className)

  const linkList = useSidebarLinks(userType, userCategory, dialogRef)

  const onToggle = toggleDialog(dialogRef)

  return (
    <Box className={menuClass} {...props}>
      {linkList.map((link, index) => (
        <MenuItem
          key={index}
          side={side}
          active={isActiveLink(link, pathname)}
          {...link}
        />
      ))}
      <Dialog ref={dialogRef} toggleDialog={onToggle} />
    </Box>
  )
}

const styles = theme => ({
  menu: {
    background: colors.white,
    width: theme.spacing(13),
    height: 'calc(100vh - 80px)',
    transition: '0.3s ease-out width, 0.3s ease-out transform',
    paddingTop: theme.spacing(2),
    overflowX: 'hidden',

    '&:hover, &.open': {
      width: theme.spacing(32),
    },

    '& .menu-full-width-item': {
      opacity: 0,

      [theme.breakpoints.down('sm')]: {
        opacity: 1,
      },
    },

    '& .menu-item-text': {
      opacity: 0,
      transition: 'ease-out 0.3s opacity',
    },

    '& .menu-item-collapsed-item': {
      display: 'block',
      alignContent: 'center',
      opacity: 1,
      transition: 'ease-out 0.3s opacity',
    },

    '&:hover .menu-item-text, &.open .menu-item-text, &.open .menu-item-text, &:hover .menu-full-width-item': {
      opacity: 1,
    },

    '&:hover .menu-item-collapsed-item, &.open .menu-item-collapsed-item': {
      opacity: 0,
      display: 'none',
    },

    '& a:hover svg, & a:hover > p': {
      transition: '100ms',
      color: `${colors.aqua500} !important`,
    },
  },
})

export default withStyles(styles)(SidebarMenu)
